import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  Company,
  CompanyTypes,
  RefactoredCompany,
  UserRoles
} from "@/jenesys-types/jenesys-entities/config";

import { selectCompanies } from "@/store/app/bootstrap/bootstrap";
import {
  selectActiveClientId,
  selectActiveCompanyId,
  selectCompany,
  selectSelectedClient,
  selectSelectedCompany,
  setActiveClientEntity,
  setActiveCompanyEntity,
  setActiveLocation
} from "@/store/features/companies/companies";

export const useCompany = () => {
  const dispatch = useDispatch();
  const { clientId, companyId } = useParams();

  const availableCompanies = useSelector(selectCompanies);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const activeClientId = useSelector(selectActiveClientId);
  const selectedCompany = useSelector(selectSelectedCompany);
  const selectedClient = useSelector(selectSelectedClient);
  const company = useSelector(selectCompany);
  const companyType = selectedClient?.type || selectedCompany?.type;

  // Helper function to convert Company to RefactoredCompany
  const toRefactoredCompany = (companyEntity: Company): RefactoredCompany => ({
    id: companyEntity.id,
    name: companyEntity.name,
    role: (companyEntity.role as UserRoles) || UserRoles.COMPANY_USER,
    type: companyEntity.type || CompanyTypes.COMPANY
  });

  // Sync URL parameters with Redux state on mount and when they change
  useEffect(() => {
    if (companyId && availableCompanies?.length > 0) {
      const foundCompany = availableCompanies.find((c) => c.id === companyId);
      if (foundCompany && !selectedCompany) {
        dispatch(setActiveCompanyEntity(toRefactoredCompany(foundCompany)));
        dispatch(setActiveLocation(companyId, clientId));
      }
    }
  }, [dispatch, companyId, availableCompanies, clientId, selectedCompany]);

  // Sync client from URL if available
  useEffect(() => {
    if (clientId && availableCompanies?.length > 0) {
      const foundClient = availableCompanies.find((c) => c.id === clientId);
      if (foundClient && !selectedClient) {
        dispatch(setActiveClientEntity(foundClient));
        dispatch(setActiveLocation(companyId || "", clientId));
      }
    }
  }, [dispatch, clientId, availableCompanies, companyId, selectedClient]);

  return {
    availableCompanies,
    selectedCompany,
    activeCompanyId,
    activeClientId,
    selectedClient,
    company,
    companyType
  };
};
